import type { AuthenticatedRequest } from '@app/rest/rest.lib';
import { authenticatedRequest } from '@app/rest/rest.lib'
import type { IApiEntry } from '@app/state/intake/helpers'

import type { IVisitWithPatient } from './visit.interface'
import type { IProgram } from '../program/program.interface'


export type IVisitsResponse = {
  status: string
  visits: IVisitWithPatient[]
  pageCount: number
  totalNumberOfRecords: number
}

export function getVisits(
  pageNumber: number,
  recordsPerPage: number,
  location?: string,
  mrn?: string,
  patientName?: string,
  status?: string[],
  visitDate?: string,
  patientId?: string,
  sortDirection?: string,
  sortField?: string,
  type?: string[],
): AuthenticatedRequest<IVisitsResponse> {
  let url = `/api/v2/visits?`

  let statusUrlParams = ''
  if (status && status.length > 0) {
    statusUrlParams = status.map((it) => `&status=${it}`).join('')
  }

  let typeUrlParams = ''
  if (type && type.length > 0) {
    typeUrlParams = type.map((it) => `&type=${it}`).join('')
  }

  const paramArray: string[][] = [
    ['pageNumber', pageNumber.toString()],
    ['recordsPerPage', recordsPerPage.toString()],
    ['location', location],
    ['mrn', mrn],
    ['patientName', patientName],
    ['visitDate', visitDate],
    ['patientId', patientId],
    ['sortDirection', sortDirection],
    ['sortField', sortField],
  ].filter((it) => {
    return it[1] !== undefined && it[1] !== null
  })

  const paramMap = new URLSearchParams(paramArray)
  const urlParams = paramMap.toString()

  url = `${url}&${urlParams}${statusUrlParams}${typeUrlParams}`

  return authenticatedRequest<IVisitsResponse>({
    method: 'get',
    url,
  })
}

export type ILocationResponse = {
  locations: Record<string, string>
}

export function getLocations(): AuthenticatedRequest<ILocationResponse> {
  const url = `/api/v2/clinic/locations`

  return authenticatedRequest<ILocationResponse>({
    method: 'get',
    url,
  })
}

export type IGetVisitResponse = {
  visit: IVisitWithPatient
}

export function getVisit(
  visitId: string,
): AuthenticatedRequest<IGetVisitResponse> {
  const url = `/api/v2/visit/${visitId}`

  return authenticatedRequest<IGetVisitResponse>({
    method: 'get',
    url,
  })
}

export type IGetVisitProgramsResponse = {
  programs: IProgram[]
}

export function getVisitPrograms(
  visitId: string,
): AuthenticatedRequest<IGetVisitProgramsResponse> {
  const url = `/api/v2/visit/${visitId}/programs`

  return authenticatedRequest<IGetVisitProgramsResponse>({
    method: 'get',
    url,
  })
}

export function requestVisitPDF(visitId: string, templateId: string) {
  return authenticatedRequest({
    method: 'get',
    responseType: 'blob',
    url: `/api/v2/visit/${visitId}/${templateId}/authenticatedPDF`,
  })
}

export interface SubmitResponsesResult {
  entry: IApiEntry
}

export function submitResponses(
  entryId: string,
  responses: Record<string, string>,
): AuthenticatedRequest<SubmitResponsesResult> {
  return authenticatedRequest({
    method: 'post',
    url: `/api/v2/entry/${entryId}/responses`,
    data: {
      responses: responses,
    },
  })
}

export interface DerivedValuesResult {
  derivedValues: Record<string, unknown>
}

export function getDerivedValues(
  visitId: string,
): AuthenticatedRequest<DerivedValuesResult> {
  return authenticatedRequest({
    method: 'get',
    url: `/api/v2/visit/${visitId}/derived-values`,
  })
}
